// frontend/src/pages/admin/ExternalTokens/AdminActivityTab.js
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { History, RefreshCw, Shield, X, XCircle } from 'lucide-react';

import { useApiAdminActivity } from '../../../hooks/useApiOperationsData';
import DebouncedSearchInput from '../../../components/UI/DebouncedSearchInput';
import { FilterButton } from '../../../components/UI/FilterButton';
import StatusBadge from '../../../components/UI/StatusBadge';
import Pagination from '../../../components/UI/Pagination';
import { formatDateTime } from '../../../utils/dateUtils';

const ACTION_OPTIONS = [
  { id: '', label: 'All actions' },
  { id: 'CREATE', label: 'Create' },
  { id: 'UPDATE', label: 'Update / Rotate' },
  { id: 'DELETE', label: 'Revoke' },
];

const actionBadge = (action) => {
  if (action === 'CREATE') return { status: 'approved', label: 'Created' };
  if (action === 'DELETE') return { status: 'rejected', label: 'Revoked' };
  if (action === 'UPDATE') return { status: 'in_progress', label: 'Updated' };
  return { status: 'draft', label: action || 'Unknown' };
};

const AdminActivityTab = () => {
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [action, setAction] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const params = useMemo(() => ({
    page,
    page_size: pageSize,
    ...(search ? { search } : {}),
    ...(action ? { action } : {}),
  }), [page, pageSize, search, action]);

  const { data, isLoading, isFetching, isError, refetch } = useApiAdminActivity(params);
  const results = data?.results || [];
  const totalPages = data?.total_pages || 1;
  const totalCount = data?.count || 0;
  const activeFilterCount = action ? 1 : 0;

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <DebouncedSearchInput
            value={search}
            onSearch={(value) => { setSearch(value); setPage(1); }}
            placeholder="Search actor, app name, or details..."
            theme="admin"
            className="w-full sm:max-w-xl sm:flex-1"
          />
          <div className="flex items-center gap-2 flex-shrink-0">
            <FilterButton
              theme="admin"
              isOpen={showFilters}
              activeCount={activeFilterCount}
              onClick={() => setShowFilters((v) => !v)}
              className="flex-shrink-0"
            />
            <button
              type="button"
              onClick={() => refetch()}
              className="icon-button-admin flex-shrink-0"
              title="Refresh"
              aria-label="Refresh admin activity"
            >
              <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            </button>
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={() => { setAction(''); setPage(1); }}
                className="icon-button-admin flex-shrink-0"
                title="Clear filters"
                aria-label="Clear filters"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            <Link
              to="/admin/system-monitoring?tab=audit"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-red-600 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 rounded ml-1"
            >
              <Shield className="w-3.5 h-3.5" />
              Full audit trail
            </Link>
          </div>
        </div>

        {showFilters && (
          <div className="flex items-center flex-wrap gap-2 pt-1">
            <span className="text-xs font-medium text-gray-600 mr-1">Action:</span>
            {ACTION_OPTIONS.map((opt) => {
              const active = action === opt.id;
              return (
                <button
                  key={opt.id || 'all'}
                  type="button"
                  aria-pressed={active}
                  onClick={() => { setAction(opt.id); setPage(1); }}
                  className={`tab-btn-admin tab-btn-admin-sm ${active ? 'active' : ''}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {isError ? (
        <div className="text-center py-12">
          <XCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Couldn&apos;t load activity</h3>
          <p className="text-sm text-gray-500 mb-4">Try again in a moment.</p>
          <button type="button" onClick={() => refetch()} className="btn btn-admin-primary">
            <RefreshCw className="w-4 h-4" /> Retry
          </button>
        </div>
      ) : isLoading ? (
        <div className="bg-white rounded-2xl shadow-sm border overflow-hidden animate-pulse">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="px-6 py-4 border-b border-gray-100 flex gap-4">
              <div className="h-4 bg-gray-200 rounded w-24" />
              <div className="h-4 bg-gray-200 rounded flex-1" />
            </div>
          ))}
        </div>
      ) : results.length === 0 ? (
        <div className="text-center py-12">
          <History className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {search || action ? 'No activity matches your filters' : 'No key lifecycle events yet'}
          </h3>
          <p className="text-sm text-gray-500">
            Creates, updates, rotations, and revokes will appear here.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
          <ul className="divide-y divide-gray-100">
            {results.map((row) => {
              const badge = actionBadge(row.action);
              return (
                <li key={row.id} className="px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-start gap-3">
                  <div className="flex-shrink-0">
                    <StatusBadge status={badge.status} label={badge.label} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <p className="text-sm font-semibold text-gray-900">
                        {row.app_name || 'Unknown key'}
                      </p>
                      {row.key_prefix && (
                        <code className="text-xs font-mono text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                          {row.key_prefix}
                        </code>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1 break-words">{row.details || '—'}</p>
                    <p className="text-xs text-gray-500 mt-1.5">
                      {formatDateTime(row.timestamp)}
                      {' · '}
                      {row.actor_name || row.actor_staff_id || 'System'}
                      {row.ip_address ? ` · ${row.ip_address}` : ''}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            totalCount={totalCount}
            pageSize={pageSize}
            theme="admin"
            noun="events"
          />
        </div>
      )}
    </div>
  );
};

export default AdminActivityTab;
